/**
 * Quote Data Converter
 * 
 * Utility functions to convert between Quote and QuoteWizardData formats
 * for unified Create/Edit functionality.
 */

import type { Quote } from '../../../types/quote';
import type { QuoteWizardData } from '../QuoteWizard';

/**
 * Calculate total sum insured from wizard data
 */
export function calculateTotalSumInsured(data: QuoteWizardData): number {
  // Calculate from participants (single source of truth)
  if (data.quote?.participants) {
    return data.quote.participants.reduce((total, participant) => {
      return total + (Number(participant.sumInsured) || 0);
    }, 0);
  }
  
  return 0;
}

/**
 * Calculate total sum insured from location data (for display in tables)
 */
export function calculateTotalSumInsuredFromLocations(locations: Array<{ sumInsured?: number }>): number {
  return locations.reduce((total, location) => total + (location.sumInsured || 0), 0);
}

/**
 * Calculate total hectares/animals insured from wizard data
 */
export function calculateTotalHectaresAnimalCount(data: QuoteWizardData): number {
  // Calculate from participants (single source of truth)
  if (data.quote?.participants) {
    return data.quote.participants.reduce((total, participant) => {
      return total + (Number(participant.hectaresAnimalCount) || 0);
    }, 0);
  }
  
  return 0;
}


// Note: Removed ExtendedQuote type - no longer needed after cleanup

/**
 * Convert Quote to QuoteWizardData for Edit mode
 */
export function convertQuoteToWizardData(quote: Quote): QuoteWizardData {
  // Note: Removed redundant participant extraction
  // All participant data is now in quote.participants (single source of truth)

  // Note: Removed extraction of underlying_activities, perils, perilWeights - now in quote object only
  
  // Extract location validation and other data
  const quoteData = quote as unknown as Record<string, unknown>;

  // Note: Removed protection levels extraction - now in quote object only

  // Note: Removed location validation extraction - now in quote object only

  // Note: Removed coverage_items extraction - now in quote object only

  // Extract participants for bulk upload data calculation
  const participants = quote.participants || [];
  
  // Unwrap ALL schema-wrapped fields for frontend consumption
  const unwrappedQuote = {
    ...quote,
    // Unwrap all JSON fields that are now consistently schema-wrapped
    underlying_assets: (quote as unknown as Record<string, { data?: unknown }>).underlyingAssets?.data || quote.underlying_assets || { activities_products: [] },
    perils_config: (quote as unknown as Record<string, { data?: unknown }>).perilsConfig?.data || quote.perils_config || { perils: [], total_weight_check: 0 },
    coverageItems: (quote as unknown as Record<string, { data?: unknown }>).coverageItems?.data || quote.coverageItems || [],
    protectionLevels: (quote as unknown as Record<string, { data?: unknown }>).protectionLevels?.data || quote.protectionLevels || {},
    locationValidation: (quote as unknown as Record<string, { data?: unknown }>).locationValidation?.data || quote.locationValidation || {},
    // calculationResults removed - quotationResults is single source of truth
    quotationResults: (quote as unknown as Record<string, { data?: unknown }>).quotationResults?.data || quote.quotationResults || null,
    selectedScenarios: (quote as unknown as Record<string, { data?: unknown }>).selectedScenarios?.data || quote.selectedScenarios || {},
    proofOfInterest: (quote as unknown as Record<string, { data?: unknown }>).proofOfInterest?.data || {},
  };
  
  // Note: Data unwrapping working correctly

  return {
    // Note: Removed createdAt, updatedAt, id, quoteId - now use quote object as single source of truth
    quote: unwrappedQuote as unknown as Quote,
    
    // Step 1: Product & Coverage Selection
    // Note: Removed productId, product, underlying_activities, coverage_items, policyType - now in quote object only
    // Note: Removed templateName - now in quote object only
    // Remove organizationDetails - organization is now in quote.participants
    // Note: Removed redundant policyholder and insuredParticipants fields
    // All participant data is now in quote.participants (single source of truth)
    bulkUploadData: quote.isMasterPolicy ? {
      uploadId: 'existing-quote',
      fileName: 'Previously uploaded data',
      status: 'completed',
      validRecords: participants.filter(p => p.role === 'INSURED').length,
      invalidRecords: 0,
    } : undefined,
    
    // Step 3: Location Verification
    // Note: Removed locationValidation - now in quote object only
    
    // Step 4: Coverage Details
    // Note: Removed coverageStartDate, coverageEndDate, perils, perilWeights - now in quote object only
    
    // Step 5: Protection Levels
    // Note: Removed protectionLevels, quotationJobId, quotationResults, selectedScenarios - now in quote object only
    recalcNeeded: false, // Initialize to false when loading existing quote
    
    // Calculate validated steps based on current progress
    // Only steps up to and including the current step should be navigable
    validatedSteps: (() => {
      const allSteps = ['product', 'participants', 'location', 'coverage', 'protection', 'payment', 'review'];
      const currentStep = quote.currentWizardStep || 1;
      // Allow navigation to steps 1 through currentStep
      return allSteps.slice(0, currentStep);
    })(),
    
    // Restore current wizard step for navigation
    currentWizardStep: quote.currentWizardStep || 1,
    
    // Proof of insurable interest (Step 2)
    proofOfInterestConfirmed: (unwrappedQuote.proofOfInterest as Record<string, unknown>)?.confirmed as boolean || false,
    proofOfInterestDocuments: (unwrappedQuote.proofOfInterest as Record<string, unknown>)?.documents as Array<{id: string; filename: string; url: string; size?: number; mimeType?: string}> || [],
    
    pricingCalculation: (() => {
      // Try to extract pricing data from quote
      const pricingData = quoteData.pricingCalculation as Record<string, unknown> | undefined;
      
      if (pricingData && typeof pricingData === 'object') {
        return {
          totalPremium: Number(pricingData.totalPremium) || 0,
          breakdown: {
            anrp: Number(pricingData.anrp) || 0,
            loadings: Number(pricingData.loadings) || 0,
            fees: Number(pricingData.fees) || 0,
            taxes: Number(pricingData.taxes) || 0,
            subsidies: Number(pricingData.subsidies) || 0,
          },
        };
      }
      
      // No pricing data available yet
      return undefined;
    })(),
    
    // Note: Removed paymentDetails - payment info is stored in quote.participants[].paymentDetails
    // Note: Removed notes, strikeLevel - now in quote object only
  };
}

/**
 * Convert QuoteWizardData back to Quote for API submission
 */
export function convertWizardDataToQuote(data: QuoteWizardData): Partial<Quote> {
  // Since data.quote is now the single source of truth, we can simply return it
  // with any necessary transformations for API compatibility
  
  if (!data.quote) {
    throw new Error('No quote data available for conversion');
  }

  return {
    ...data.quote,
    // Override premium amount from pricing calculation if available
    premiumAmount: data.pricingCalculation?.totalPremium || data.quote.premiumAmount || 0,
  };
}

/**
 * Get the initial data for wizard based on mode
 */
export function getInitialWizardData(mode: 'create' | 'edit', existingQuote?: Quote): QuoteWizardData {
  if (mode === 'edit' && existingQuote) {
    return convertQuoteToWizardData(existingQuote);
  }
  
  // Create mode - return completely empty initial data
  // This ensures we don't inherit data from previous sessions
  return {
    // Completely empty - no quote object, no inherited data
    // This forces the wizard to start fresh
  };
}
